/* knowledge-card-body.tsx generated by @compiled/babel-plugin v0.38.1 */
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./knowledge-card-body.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Suspense, useState } from 'react';
import { useIntl } from 'react-intl-next';
import Button, { LinkButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { SmartCardProvider as Provider } from '@atlaskit/link-provider';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { getSmartLinksAnalyticsContextData } from '../../ai-answer-dialog/analytics';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../../common/controller/analytics';
import { ErrorBoundary } from '../../common/ui/error-boundary';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';
var CardBodyWrapper = {
  default: "_kqswh2mm _u5f3pxbi _19bvpxbi"
};
var ColumnBottomPadding = {
  default: "_n3td1b66"
};
var DynamicRowStyling = {
  column: "_1e0c1txw _2lx21bp4 _1p571b66 _gy1p1b66",
  grid: "_1e0c11p5 _yv0ewxkt _gy1ppxbi _1p571b66"
};
var EXPANDED_MAX_ITEMS_SHOWN = 6;
var MAX_ITEMS_SHOWN = 2;
var SINGLE_COLUMN_MAX_ITEMS_SHOWN = 4; // 2 rows * 2 items per row
var SINGLE_COLUMN_EXPANDED_MAX_ITEMS_SHOWN = 12; // 6 rows * 2 items per row

export var KnowledgeCardBody = /*#__PURE__*/React.memo(function (_ref) {
  var columnsConfig = _ref.columnsConfig,
    profileUrl = _ref.profileUrl,
    footerButtonText = _ref.footerButtonText,
    footerButtonAction = _ref.footerButtonAction,
    testId = _ref.testId;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setExpanded = _useState2[1];

  // Determine if we should show a single column layout (when only one column has data)
  var hasDataColumns = columnsConfig.filter(function (config) {
    return config.data.length > 0;
  });
  var showSingleColumn = hasDataColumns.length === 1;

  // Calculate total items across all columns for expand logic
  var totalItems = columnsConfig.reduce(function (sum, config) {
    return sum + config.data.length;
  }, 0);
  var shouldShowExpandButton = showSingleColumn ? totalItems > SINGLE_COLUMN_MAX_ITEMS_SHOWN : totalItems > MAX_ITEMS_SHOWN * columnsConfig.length;
  return /*#__PURE__*/React.createElement(Box, {
    xcss: CardBodyWrapper.default,
    testId: testId
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.200"
  }, columnsConfig.map(function (columnConfig, columnIndex) {
    if (columnConfig.data.length === 0) {
      return null;
    }
    var maxItems = !isExpanded ? showSingleColumn ? SINGLE_COLUMN_MAX_ITEMS_SHOWN : MAX_ITEMS_SHOWN : Math.min(columnConfig.data.length, showSingleColumn ? SINGLE_COLUMN_EXPANDED_MAX_ITEMS_SHOWN : EXPANDED_MAX_ITEMS_SHOWN);
    return /*#__PURE__*/React.createElement(Stack, {
      key: columnIndex,
      grow: "fill",
      space: "space.050",
      xcss: ColumnBottomPadding.default
    }, /*#__PURE__*/React.createElement(Box, {
      paddingInlineStart: "space.200",
      paddingInlineEnd: "space.200",
      paddingBlockEnd: "space.075"
    }, /*#__PURE__*/React.createElement(Inline, {
      spread: "space-between",
      alignBlock: "center"
    }, /*#__PURE__*/React.createElement(Heading, {
      size: "xsmall"
    }, columnConfig.header), /*#__PURE__*/React.createElement(Link, {
      href: columnConfig.viewAllLinkHref,
      appearance: "subtle",
      onClick: function onClick(e) {
        columnConfig.viewAllLinkOnClick(e);
      }
    }, /*#__PURE__*/React.createElement(Text, {
      color: "color.text.subtle"
    }, formatMessage(messages.knowledge_cards_view_all_link))))), /*#__PURE__*/React.createElement(ErrorBoundary, {
      fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
    }, /*#__PURE__*/React.createElement(Suspense, {
      fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
    }, /*#__PURE__*/React.createElement(Provider, null, /*#__PURE__*/React.createElement(Box, {
      xcss: showSingleColumn ? DynamicRowStyling.grid : DynamicRowStyling.column
    }, columnConfig.data.slice(0, maxItems).map(function (item, index) {
      // Calculate if this item is shown after card body expansion
      // For single column layout: items with index >= 4 are shown after expansion
      // For multi-column layout: items with index >= 2 are shown after expansion
      var shownAfterCardBodyExpanded = showSingleColumn ? index >= SINGLE_COLUMN_MAX_ITEMS_SHOWN && isExpanded : index >= MAX_ITEMS_SHOWN && isExpanded;
      return /*#__PURE__*/React.createElement(columnConfig.component, {
        key: index,
        item: item,
        maxItems: maxItems,
        onClickCallback: columnConfig !== null && columnConfig !== void 0 && columnConfig.onClickCallback ? function () {
          return columnConfig.onClickCallback(index, shownAfterCardBodyExpanded);
        } : undefined,
        smartLinksAnalyticsContextData: columnConfig.smartLinksAnalyticsContextLocation ? getSmartLinksAnalyticsContextData({
          location: columnConfig.smartLinksAnalyticsContextLocation,
          commonAttributes: commonAttributes
        }) : undefined
      });
    }))))));
  })), /*#__PURE__*/React.createElement(Box, null, shouldShowExpandButton ? /*#__PURE__*/React.createElement(Inline, {
    alignInline: "center",
    space: "space.025"
  }, isExpanded ? /*#__PURE__*/React.createElement(LinkButton, {
    href: profileUrl || '',
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        location: 'footer-view-all'
      })));
      fireSAINUsageEvent({
        metCriteria: footerButtonAction
      });
    },
    appearance: "subtle"
  }, /*#__PURE__*/React.createElement(Text, {
    color: "color.text.subtlest",
    weight: "medium"
  }, footerButtonText)) : /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      setExpanded(function (e) {
        return !e;
      });
      fireAnalyticsEvent(onKnowledgeCardLinkClick(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        location: 'show-more'
      })));
      fireSAINUsageEvent({
        metCriteria: SainAction.EXPAND_CLICKED
      });
    },
    appearance: "subtle"
  }, /*#__PURE__*/React.createElement(Text, {
    color: "color.text.subtlest",
    weight: "medium"
  }, formatMessage(messages.ai_answer_expand_button_label)))) : null));
});